import { useState, ChangeEventHandler } from 'react'
import { CheckboxButton, TextArea } from '../../../Components/Form/Inputs'
import FormPage from '../../../Components/Form/Page/FormPage'
import LoadingSpinner from '../../../Components/LoadingSpinner'
import useBriefContext from '../../../Hooks/Brief/useBriefContext'
import throwGQLError from '../../../Utilities/Urql/Errors/throwGQLError'
import InputView from '../InputView'
import { FacilitiesQuery, useFacilitiesQuery } from './FacilitiesInput.generated'
import './FacilitiesInput.less'

const facilityIcon: Record<string, string> = {
  'Air Conditioning': '❄️',
  'Audio Equipment': '🔊',
  'Breakout Spaces': '💥',
  'Close to Transport Links': '🚎',
  'Disabled Access': '♿️',
  'Natural Light': '☀️',
  'Outdoor Space': '🌻',
  Parking: '🚗',
  'TV/Projector': '🖥',
  'Whiteboards/Flipcharts': '🎨',
}

interface FacilitiesFieldsetProps {
  fetching: boolean
  facilities?: FacilitiesQuery['collection']['features']
  selectedFacilityIDs: number[]
  onFacilitySelect: ChangeEventHandler<HTMLInputElement>
}

const FacilitiesFieldset = ({
  facilities,
  selectedFacilityIDs,
  onFacilitySelect,
  fetching,
}: FacilitiesFieldsetProps): JSX.Element => (
  <fieldset aria-busy={fetching} aria-describedby={'facilities-loading'}>
    {fetching ? (
      <LoadingSpinner progressId='facilities-loading' />
    ) : (
      <>
        <legend className='visually-hidden'>Optional venue facilities:</legend>
        <ul role='list' className='facilities'>
          {facilities &&
            facilities.map(({ id: facilityID, name }) => (
              <li key={facilityID}>
                <CheckboxButton
                  id={`facility-input-${facilityID}`}
                  name={name}
                  value={facilityID}
                  checked={selectedFacilityIDs.includes(parseInt(facilityID, 10))}
                  onChange={onFacilitySelect}
                  label={`${name} ${facilityIcon[name]}`}
                />
              </li>
            ))}
        </ul>
      </>
    )}
  </fieldset>
)

const CateringAndFacilitiesInput: InputView = ({ path }) => {
  const { brief } = useBriefContext()
  const [{ fetching, error, data }] = useFacilitiesQuery({
    pause: brief.collection === undefined,
    requestPolicy: 'cache-first',
    variables: { id: brief.collection ?? '' },
  })

  const [facilityIDs, setFacilityIDs] = useState(brief.spaceFacilityIds ?? [])
  const [cateringNeeds, setCateringNeeds] = useState(brief.cateringNeeds ?? '')

  if (error) throwGQLError(error)

  const { features } = data?.collection ?? { features: [] }

  const onFacilityChange: ChangeEventHandler<HTMLInputElement> = ({ target }) => {
    const intID = parseInt(target.value, 10)
    const modifiedIDs = target.checked
      ? [...facilityIDs, intID]
      : facilityIDs.filter((id) => id !== intID)
    setFacilityIDs(modifiedIDs)
  }

  const onTextAreaChange: ChangeEventHandler<HTMLTextAreaElement> = ({ target }) => {
    setCateringNeeds(target.value)
  }

  return (
    <FormPage
      metaTitle='Facilities & catering - HeadBox'
      heading='Need anything specific?'
      path={path}
      inputAttributes={{ spaceFacilityIds: facilityIDs, cateringNeeds }}
      className='facilities-page'>
      <FacilitiesFieldset
        facilities={features}
        fetching={fetching}
        selectedFacilityIDs={facilityIDs}
        onFacilitySelect={onFacilityChange}
      />
      <CateringInput cateringNeeds={cateringNeeds} onChange={onTextAreaChange} />
    </FormPage>
  )
}

interface CateringInputProps {
  cateringNeeds: string
  onChange: ChangeEventHandler<HTMLTextAreaElement>
}
const CateringInput = ({ cateringNeeds, onChange }: CateringInputProps): JSX.Element => (
  <section className='catering'>
    <h3>Do you have any catering requirements?</h3>
    <label htmlFor='catering-needs' className='visually-hidden'>
      Catering Requirement Notes
    </label>
    <TextArea
      id='catering-needs'
      rows={5}
      value={cateringNeeds}
      onChange={onChange}
      placeholder={
        'If you have any food and drink requests or other requirements, please write them here.'
      }
    />
  </section>
)

export default CateringAndFacilitiesInput
